import { useEffect, useMemo } from 'react';

import { Grid, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';

import StyledForm from 'components/common/styled-form';
import DialogModal from 'components/modals/dialog-modal';
import { PlanModalBody } from './plan-modal-body';

import { FormProvider, useForm } from 'react-hook-form';

import { SwitchController } from 'controllers/switch-controller';

import { yupResolver } from '@hookform/resolvers/yup';

import { PlansValidationSchema } from './validation';
import {
  getPlanFormDefaultValues,
  mapFormValuesToCreatePayload,
  mapFormValuesToUpdatePayload,
  PlanModalState,
} from './utils';

import { planModalStyles } from './styles';
import { filledButtonStyles, outlinedButtonStyles, flexAlignCenter } from 'globalStyles';
import useToast from 'hooks/useToast';

import { PlanFormValues } from 'utils/types';
import { useCreatePlanMutation, useUpdatePlanMutation } from 'redux/apis/users/users-api';

interface IProps {
  open: boolean;
  modalState: PlanModalState;
  handleClose: () => void;
}

const PlanVisibleSwitch = () => (
  <Grid sx={[flexAlignCenter, { gap: '4px' }]}>
    <SwitchController shouldUnregister name="isVisible" isSmall />
    <Typography sx={planModalStyles.visibleText}>Visible</Typography>
  </Grid>
);

export const PlanModal = ({ open, handleClose, modalState }: IProps) => {
  const showToast = useToast();
  const isEditMode = modalState.mode === 'edit';

  const [createPlan, { isLoading: isCreating }] = useCreatePlanMutation();
  const [updatePlan, { isLoading: isUpdating }] = useUpdatePlanMutation();

  const defaultValues = useMemo(() => getPlanFormDefaultValues(modalState), [modalState]);

  const formMethods = useForm<PlanFormValues>({
    reValidateMode: 'onSubmit',
    mode: 'onSubmit',
    resolver: yupResolver(PlansValidationSchema),
    defaultValues,
  });

  useEffect(() => {
    formMethods.reset(getPlanFormDefaultValues(modalState));
  }, [modalState, formMethods]);

  const handleApply = async (data: PlanFormValues) => {
    try {
      if (isEditMode) {
        await updatePlan({
          planId: modalState.plan.id,
          body: mapFormValuesToUpdatePayload(data),
        }).unwrap();
        showToast({ type: 'success', message: 'Plan updated successfully.' });
      } else {
        await createPlan(mapFormValuesToCreatePayload(data)).unwrap();
        showToast({ type: 'success', message: 'Plan created successfully.' });
      }

      handleClose();
    } catch (error: any) {
      showToast({
        type: 'error',
        message: error?.data?.message || 'Something went wrong',
      });
    }
  };

  return (
    <DialogModal
      withoutPadding
      maxWidth="650px"
      width="100%"
      withCloseButton={false}
      contentSx={planModalStyles.dialogModal}
      open={open}
      handleClose={handleClose}
    >
      <FormProvider {...formMethods}>
        <StyledForm onSubmit={formMethods.handleSubmit(handleApply)}>
          <Grid container alignItems="center" sx={planModalStyles.formHeader}>
            <Typography sx={planModalStyles.planHeaderText}>
              {isEditMode ? 'Edit Plan' : 'Create Plan'}
            </Typography>
            <PlanVisibleSwitch />
          </Grid>
          <Grid sx={planModalStyles.planModalBody}>
            <PlanModalBody isEditMode={isEditMode} />
            <Grid container justifyContent="flex-end" sx={planModalStyles.buttonContainer}>
              <LoadingButton
                sx={[outlinedButtonStyles, planModalStyles.cancelButton]}
                type="button"
                onClick={handleClose}
                disabled={isCreating || isUpdating}
              >
                Cancel
              </LoadingButton>
              <LoadingButton
                sx={[filledButtonStyles, planModalStyles.createButton]}
                type="submit"
                loading={isCreating || isUpdating}
              >
                {isEditMode ? 'Save' : 'Create'}
              </LoadingButton>
            </Grid>
          </Grid>
        </StyledForm>
      </FormProvider>
    </DialogModal>
  );
};
